import { searchParam, useSetSearch } from '@luminovo/design-system';
import { createFileRoute } from '@tanstack/react-router';
import type { JSX } from 'react';
import { z } from 'zod/v4';

import { OrdersPageForUser } from '@/areas/orders/Orders/pages/OrdersPageForUser';
import { canViewOrders } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';
import { LayoutCard } from '@/shared/ui/LayoutCard';
import { SidebarPageContent } from '@/shared/ui/PageLayoutCollapsibleSidebar';

// One route, so the list stays mounted. Links to the list pass `orderId: undefined`, or the router keeps the id.
// The page, from `orders-ccp`, doesn't know the app's routes: this file reads the filters and sort and passes a writer.
export const Route = createFileRoute('/_authenticated/orders/{-$orderId}')({
    staticData: { access: { permissions: canViewOrders }, chrome: 'home' } satisfies PageStaticData,
    validateSearch: z.object({
        poNumber: z.string().optional(),
        customerName: z.string().optional(),
        customerPartNumber: z.string().optional(),
        sortBy: searchParam.enum(['order_number', 'date'], 'optional'),
        sortDirection: searchParam.enum(['asc', 'desc'], 'optional'),
    }),
    component: RouteComponent,
});

function RouteComponent(): JSX.Element {
    const pathParams = Route.useParams();
    const poNumber = Route.useSearch({ select: (search) => search.poNumber });
    const customerName = Route.useSearch({ select: (search) => search.customerName });
    const customerPartNumber = Route.useSearch({ select: (search) => search.customerPartNumber });
    const sortBy = Route.useSearch({ select: (search) => search.sortBy });
    const sortDirection = Route.useSearch({ select: (search) => search.sortDirection });
    const setSearch = useSetSearch();
    return (
        <SidebarPageContent layoutVariant="fullWidth">
            <LayoutCard title={undefined} style={{ height: '100%' }}>
                <OrdersPageForUser
                    selectedOrderId={pathParams.orderId}
                    search={{ poNumber, customerName, customerPartNumber, sortBy, sortDirection }}
                    onSearchChange={setSearch}
                />
            </LayoutCard>
        </SidebarPageContent>
    );
}
